/**
 * Variant-Bundle Synchronization Script
 * 
 * This script synchronizes variant selection between:
 * 1. product-variant-picker.liquid (main variant selector)
 * 2. product-buttons.liquid (bundle variant selectors)
 * 
 * Rules:
 * - When #1 variant is selected → #2 and #3 change accordingly
 * - When #2 variant is selected → #3 changes but #1 doesn't change
 * - When #3 variant is selected → #1 and #2 don't change
 */

// Shared utilities for bundle functionality
window.BundleUtils = {
  // Update custom dropdown UI
  updateCustomDropdownUI(dropdown, newValue) {
    const dropdownText = dropdown.querySelector('.dropdown-text');
    const dropdownItems = dropdown.querySelectorAll('.dropdown-item');

    // Update displayed text
    if (dropdownText) {
      dropdownText.textContent = newValue;
    }

    // Update selected state
    dropdownItems.forEach(item => {
      item.classList.remove('selected');
      if (item.getAttribute('data-value') === newValue) {
        item.classList.add('selected');
      }
    });
  },

  // Get main variant options from variant picker
  getMainVariantOptions() {
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return [];

    const options = [];
    const inputs = variantSelects.querySelectorAll('.product-form__input');

    inputs.forEach((input) => {
      const typeElement = input.querySelector('.product-form__input__type');
      if (!typeElement) return;

      const type = typeElement.dataset.type;
      let value = null;

      if (type === 'dropdown') {
        const select = input.querySelector('.select__select');
        value = select ? select.value : null;
      } else if (type === 'swatches') {
        const checked = input.querySelector('input[type="radio"]:checked');
        value = checked ? checked.value : null;
      } else {
        const checked = input.querySelector('input:checked');
        value = checked ? checked.value : null;
      }

      if (value) {
        options.push(value);
      }
    });

    return options;
  },

  // Check if main variant picker is sold out
  checkMainVariantSoldOut() {
    // Method 1: Check current variant availability
    const currentVariantId = this.getCurrentVariantId();
    if (currentVariantId) {
      const isAvailable = this.isVariantAvailable(parseInt(currentVariantId));
      if (!isAvailable) {
        return true;
      }
    }

    // Method 2: Check add to cart button state
    const addToCartBtn = document.querySelector('[data-add-to-cart]');
    if (addToCartBtn && addToCartBtn.disabled) {
      const btnText = addToCartBtn.querySelector('[data-add-to-cart-text]');
      if (btnText && btnText.textContent.includes('Sold Out')) {
        return true;
      }
    }

    // Method 3: Check variant selects element
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      const selectors = variantSelects.querySelectorAll('select, input[type="radio"]:checked');
      for (let selector of selectors) {
        if (selector.disabled || selector.classList.contains('sold-out')) {
          return true;
        }
      }
    }

    return false;
  },

  // Get current variant ID from main picker
  getCurrentVariantId() {
    // Method 1: Try to get from hidden variant input
    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput && variantInput.value) {
      return variantInput.value;
    }

    // Method 2: Try to get from form data
    const mainForm = document.querySelector('form[data-product-form]');
    if (mainForm) {
      const formVariantInput = mainForm.querySelector('input[name="id"]');
      if (formVariantInput && formVariantInput.value) {
        return formVariantInput.value;
      }
    }

    // Method 3: Try to get from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const variantParam = urlParams.get('variant');
    if (variantParam) {
      return variantParam;
    }

    return null;
  },

  // Check if a specific variant is available
  isVariantAvailable(variantId) {
    const allVariants = window.productVariants || [];
    const variant = allVariants.find(v => v.id === variantId);
    return variant ? variant.available : false;
  },

  // Get variant ID from a variant row
  getVariantIdFromRow(row) {
    // Method 1: Check for hidden input with variant ID
    const variantInput = row.querySelector('input[name="id"]');
    if (variantInput && variantInput.value) {
      return variantInput.value;
    }

    // Method 2: Check for data-variant-id attribute
    const variantIdAttr = row.getAttribute('data-variant-id');
    if (variantIdAttr) {
      return variantIdAttr;
    }

    // Method 3: Check variant picker within the row
    const variantPicker = row.querySelector('variant-picker, variant-selects');
    if (variantPicker) {
      const pickerVariantInput = variantPicker.querySelector('input[name="id"]');
      if (pickerVariantInput && pickerVariantInput.value) {
        return pickerVariantInput.value;
      }
    }

    // Method 4: For bundle rows, get variant ID from selected options
    const bundleSelects = row.querySelectorAll('select[name*="bundle"][name*="option"]');
    if (bundleSelects.length > 0) {
      const options = [];
      bundleSelects.forEach(select => {
        if (select.value) {
          options.push(select.value);
        }
      });
      
      if (options.length > 0) {
        const allVariants = window.productVariants || [];
        for (let variant of allVariants) {
          let match = true;
          for (let i = 0; i < options.length; i++) {
            if (variant.options[i] !== options[i]) {
              match = false;
              break;
            }
          }
          if (match) {
            return variant.id.toString();
          }
        }
      }
    }

    // Method 5: Check for select element with variant ID
    const selectElement = row.querySelector('select[name="id"]');
    if (selectElement && selectElement.value) {
      return selectElement.value;
    }

    return null;
  },

  // Find variant ID based on selected options
  findVariantId(selectedOptions) {
    const allVariants = window.productVariants || [];

    for (let variant of allVariants) {
      let match = true;

      for (let i = 0; i < selectedOptions.length; i++) {
        const optionValue = selectedOptions[i];
        const variantOption = variant[`option${i + 1}`];

        if (optionValue && variantOption && optionValue !== variantOption) {
          match = false;
          break;
        }
      }

      if (match) {
        return variant.id;
      }
    }

    return null;
  },

  // Validate bundle selection
  validateBundleSelection() {
    const selectedBundle = document.querySelector('input[name="bundle_choice"]:checked');
    if (!selectedBundle) {
      return false;
    }

    const bundleCount = parseInt(selectedBundle.value);
    const bundleVariants = document.getElementById(`bundle-${bundleCount}-variants`);

    if (!bundleVariants) {
      return false;
    }

    // Check if all required variants are selected
    const variantRows = bundleVariants.querySelectorAll('.variant-row');
    for (let row of variantRows) {
      const selects = row.querySelectorAll('.variant-select');
      for (let select of selects) {
        if (!select.value || select.value.trim() === '') {
          return false;
        }
      }
    }

    return true;
  },

  // Get bundle variants data
  getBundleVariantsData() {
    const selectedBundle = document.querySelector('input[name="bundle_choice"]:checked');
    if (!selectedBundle) return null;

    const bundleCount = parseInt(selectedBundle.value);
    const bundleVariants = [];

    for (let item = 1; item <= bundleCount; item++) {
      const itemOptions = [];

      // Get all options for this item
      for (let optionIndex = 1; optionIndex <= 10; optionIndex++) {
        const select = document.querySelector(`select[name="bundle${bundleCount}_option${optionIndex}_${item}"]`);
        if (select) {
          itemOptions.push(select.value);
        } else {
          break; // No more options
        }
      }

      // Find variant ID for this combination
      const variantId = this.findVariantId(itemOptions);

      if (variantId) {
        bundleVariants.push({
          id: variantId,
          quantity: 1,
          options: itemOptions
        });
      } else {
        return null;
      }
    }

    return {
      bundleCount: bundleCount,
      variants: bundleVariants
    };
  }
};

document.addEventListener('DOMContentLoaded', function() {

    // Configuration
    const SYNC_CONFIG = {
      debug: true,
      retryDelay: 100,
      maxRetries: 10
    };
  
    // Main synchronization class
    class VariantBundleSync {
      constructor() {
        this.isUpdating = false;
        this.variantSelects = null;
        this.bundleDropdowns = [];
        this.currentBundle = null;
        this.initializeSync();
      }
  
      initializeSync() {
        this.findElements();
        this.setupEventListeners();
      }
  
      findElements() {
        // Find variant-selects element (main product variant picker)
        this.variantSelects = document.querySelector('variant-selects');
        if (!this.variantSelects) {
          return;
        }
  
        // Find bundle dropdowns
        this.bundleDropdowns = document.querySelectorAll('.bundle-option .custom-dropdown');
  
        // Find current selected bundle
        this.getCurrentBundle();
      }
  
      getCurrentBundle() {
        const selectedBundle = document.querySelector('input[name="bundle_choice"]:checked');
        if (selectedBundle) {
          this.currentBundle = parseInt(selectedBundle.value);
        }
      }
  
      setupEventListeners() {
        // Listen for variant changes from main variant picker
        if (this.variantSelects) {
          this.variantSelects.addEventListener('change', (e) => {
            if (!this.isUpdating) {
              this.syncMainVariantToBundles();
            }
          });
        }
  
        // Listen for bundle selection changes
        document.addEventListener('change', (e) => {
          if (e.target.name === 'bundle_choice') {
            this.currentBundle = parseInt(e.target.value);
            this.refreshBundleDropdowns();
            // Update variant row sold out status when bundle selection changes
          }
        });
  
        // Listen for bundle variant dropdown changes
        document.addEventListener('change', (e) => {
          if (e.target.classList.contains('variant-select') && !this.isUpdating) {
            this.syncBundleVariants(e.target);
          }
        });
      }
  
      syncMainVariantToBundles() {
        if (!this.variantSelects) return;
  
        this.isUpdating = true;
  
        try {
          // Get current variant options from main picker
          const mainOptions = this.getMainVariantOptions();
          if (!mainOptions.length) {
            return;
          }
    
          // Sync to ALL bundles (BUY 1, BUY 2, BUY 3)
          this.updateAllBundleVariants(1, mainOptions); // Update item #1 in all bundles
          this.updateAllBundleVariants(2, mainOptions); // Update item #2 in all bundles
          this.updateAllBundleVariants(3, mainOptions); // Update item #3 in all bundles
  
        } finally {
          setTimeout(() => {
            this.isUpdating = false;
          }, 50);
        }
      }
  
      syncBundleVariants(changedSelect) {
        this.isUpdating = true;
  
        try {
          // Determine which item number was changed
          const itemNumber = this.getItemNumberFromSelect(changedSelect);
          if (!itemNumber) {

            return;
          }
  

  
          // Get the changed variant options
          const changedOptions = this.getBundleItemOptions(itemNumber, changedSelect);

  
          // Apply rules based on which item was changed - sync across ALL bundles
          if (itemNumber === 1) {
            // Rule: #1 changes → #2 and #3 change in all bundles, update main picker too
            this.updateMainVariantFromBundle(changedOptions);
            this.updateAllBundleVariants(1, changedOptions); // Update item #1 in all bundles
            this.updateAllBundleVariants(2, changedOptions); // Update item #2 in all bundles
            this.updateAllBundleVariants(3, changedOptions); // Update item #3 in all bundles
          } else if (itemNumber === 2) {
            // Rule: #2 changes → #3 changes in all bundles, update main picker too
            this.updateMainVariantFromBundle(changedOptions);
            this.updateAllBundleVariants(2, changedOptions); // Update item #2 in all bundles
            this.updateAllBundleVariants(3, changedOptions); // Update item #3 in all bundles
          } else if (itemNumber === 3) {
            // Rule: #3 changes → sync item #3 across all bundles, update main picker too
            this.updateMainVariantFromBundle(changedOptions);
            this.updateAllBundleVariants(3, changedOptions); // Update item #3 in all bundles

          }
  
        } finally {
          setTimeout(() => {
            this.isUpdating = false;
          }, 50);
        }
      }
  
      getMainVariantOptions() {
        return window.BundleUtils.getMainVariantOptions();
      }
  
      updateMainVariantFromBundle(options) {
        if (!this.variantSelects || !options.length) return;
  
        const inputs = this.variantSelects.querySelectorAll('.product-form__input');
        
        inputs.forEach((input, index) => {
          if (index >= options.length) return;
  
          const typeElement = input.querySelector('.product-form__input__type');
          if (!typeElement) return;
  
          const type = typeElement.dataset.type;
          const newValue = options[index];
  
          if (type === 'dropdown') {
            const select = input.querySelector('.select__select');
            if (select && select.value !== newValue) {
              select.value = newValue;
              select.dispatchEvent(new Event('change', { bubbles: true }));
            }
          } else if (type === 'swatches') {
            const targetInput = input.querySelector(`input[value="${newValue}"]`);
            if (targetInput && !targetInput.checked) {
              targetInput.checked = true;
              targetInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
          } else {
            // pills or other types
            const targetInput = input.querySelector(`input[value="${newValue}"]`);
            if (targetInput && !targetInput.checked) {
              targetInput.checked = true;
              targetInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        });
      }
  
      getItemNumberFromSelect(select) {
        // Extract item number from select name attribute
        // Format: bundle{X}_option{Y}_{Z} where Z is the item number
        const name = select.name;
        const match = name.match(/bundle\d+_option\d+_(\d+)/);
        return match ? parseInt(match[1]) : null;
      }
  
      getBundleNumberFromSelect(select) {
        // Extract bundle number from select name attribute
        // Format: bundle{X}_option{Y}_{Z} where X is the bundle number
        const name = select.name;
        const match = name.match(/bundle(\d+)_option\d+_\d+/);
        return match ? parseInt(match[1]) : null;
      }
  
      getBundleItemOptions(itemNumber, changedSelect = null) {
        // If we have a changed select, get options from that bundle
        // Otherwise, try to use currentBundle or find any available bundle
        let bundleNumber = this.currentBundle;
        
        if (changedSelect) {
          bundleNumber = this.getBundleNumberFromSelect(changedSelect);
        }
        
        if (!bundleNumber) return [];
  
        const options = [];
        let optionIndex = 1;
  
        while (true) {
          const select = document.querySelector(`select[name="bundle${bundleNumber}_option${optionIndex}_${itemNumber}"]`);
          if (!select) break;
  
          options.push(select.value);
          optionIndex++;
        }
  
        return options;
      }
  
      updateBundleVariants(itemNumber, options) {
        if (!this.currentBundle || !options.length) return;
  
       
  
        options.forEach((optionValue, optionIndex) => {
          const select = document.querySelector(`select[name="bundle${this.currentBundle}_option${optionIndex + 1}_${itemNumber}"]`);
          if (select && select.value !== optionValue) {
            // Update hidden select
            select.value = optionValue;
  
            // Update custom dropdown UI
            const customDropdown = select.closest('.custom-dropdown');
            if (customDropdown) {
              this.updateCustomDropdownUI(customDropdown, optionValue);
            }
  
            // Dispatch change event
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
  
      updateAllBundleVariants(itemNumber, options) {
        if (!options.length) return;
  
       
  
        // Update across all three bundles (BUY 1, BUY 2, BUY 3)
        [1, 2, 3].forEach(bundleNumber => {
          options.forEach((optionValue, optionIndex) => {
            const select = document.querySelector(`select[name="bundle${bundleNumber}_option${optionIndex + 1}_${itemNumber}"]`);
            if (select && select.value !== optionValue) {
              // Update hidden select
              select.value = optionValue;
  
              // Update custom dropdown UI
              const customDropdown = select.closest('.custom-dropdown');
              if (customDropdown) {
                this.updateCustomDropdownUI(customDropdown, optionValue);
              }
  
              // Dispatch change event
              select.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        });
      }
  
      updateCustomDropdownUI(dropdown, newValue) {
        return window.BundleUtils.updateCustomDropdownUI(dropdown, newValue);
      }
  
      refreshBundleDropdowns() {
        // Re-find bundle dropdowns when bundle selection changes
        setTimeout(() => {
          this.findElements();
        }, 100);
      }

      // Use shared utilities
      checkMainVariantSoldOut() {
        return window.BundleUtils.checkMainVariantSoldOut();
      }

      getCurrentVariantId() {
        return window.BundleUtils.getCurrentVariantId();
      }

      isVariantAvailable(variantId) {
        return window.BundleUtils.isVariantAvailable(variantId);
      }

      getVariantIdFromRow(row) {
        return window.BundleUtils.getVariantIdFromRow(row);
      }
    }
  
    // Initialize synchronization with retry mechanism
    function initializeWithRetry(retryCount = 0) {
      if (retryCount >= SYNC_CONFIG.maxRetries) {
       
        return;
      }
  
      // Check if required elements exist
      const variantSelects = document.querySelector('variant-selects');
      const bundleOptions = document.querySelector('.bundle-options');
  
      if (variantSelects && bundleOptions) {
        new VariantBundleSync();
      } else {
        setTimeout(() => {
          initializeWithRetry(retryCount + 1);
        }, SYNC_CONFIG.retryDelay);
      }
    }
  
    // Start initialization
    initializeWithRetry();
  });